import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import CatchmentDto from 'catchment/dto/CatchmentDto'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getLabel } from 'utils/StoreUtils'
import { calculateAverage, REMARK_CODES, searchMaxAnalysis, searchP90Analysis } from 'utils/AnalyseUtils'
import useApplicationSetting, { listStringParser } from 'utils/customHook/useApplicationSetting'
import i18n from 'simple-react-i18n'
import { groupBy, round, keys, uniq, compact, template, orderBy, flatten, maxBy, keyBy, sumBy, isEmpty } from 'lodash'
import CatchmentPointDto from 'catchment/dto/CatchmentPointDto'
import { push } from '@lagunovsky/redux-react-router'
import DtoAnalysisUltraLight from 'quality/dto/analyse/DtoAnalysisUltraLight'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import { THRESHOLD_COLOR, THRESHOLD_DISCRIMINANT, THRESHOLD_DISPLAY, THRESHOLD_TYPE, calculateThresholdsResult } from 'utils/QualityThresholdUtils'
import { THRESHOLD } from 'quality/constants/QualityConstants'
import { CardTable, ALIGN } from 'components/datatable/NewTable'
import { darkBlue, whiteColor } from 'utils/constants/ColorTheme'
import { NITRATES_CODE, DEFAULT_THRESHOLD_NITRATES, DEFAULT_THRESHOLD_PESTICIDES_1, DEFAULT_THRESHOLD_PESTICIDES_2, CATCHMENT_LIST_SUM_PESTICIDES, CATCHMENT_PESTICIDES, CATCHMENT_IRRELEVANT_PESTICIDES, SUM_PESTICIDES_LABO } from 'catchment/constants/CatchmentConstant'
import { getBeginingOfTheYear, getEndOfTheYear, getYear } from 'utils/DateUtil'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import SliderPanel from 'components/slider/SliderPanel'
import { exportFile } from 'utils/ExportDataUtil'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import { getNitratesThresholdColor, getPesticidesThresholdColor } from 'catchment/utils/CatchmentUtil'
import { Tooltip } from '@mui/material'

const THRESHOLD_S1 = 'CATCHMENT_PESTICIDES_DISTRIBUTION_THRESHOLD_1'
const THRESHOLD_S2 = 'CATCHMENT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLD_1'
const THRESHOLD_S3 = 'CATCHMENT_PESTICIDES_DISTRIBUTION_THRESHOLD_2'
const THRESHOLD_S4 = 'CATCHMENT_SUM_PESTICIDES_DISTRIBUTION_THRESHOLD_2'

const getTooltip = (substance, analyse, threshold1, threshold2 = undefined, showCode = true) => {
    if (!substance || !analyse || !threshold1) return {}
    const displayCode = showCode ? `[${NITRATES_CODE}]` : ''
    return {
        tooltip: (
            <TooltipAnalyse
                title={substance === 'Nitrates' ? `${i18n.nitrates} ${displayCode}` : substance}
                analyse={analyse}
                threshold={{
                    parameterCode: analyse.parameter,
                    threshold1,
                    threshold2,
                    thresholdType: THRESHOLD_TYPE.INCREASING,
                    display: THRESHOLD_DISPLAY.INCREASING,
                    discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
                }}
                overrideColors={compact([THRESHOLD_COLOR.green, threshold2 && THRESHOLD.orange, THRESHOLD_COLOR.red])}
            />
        ),
    }
}

const getAverageTooltip = (result, code, threshold1, threshold2 = undefined, showCode = true) => {
    if (!result || !code || !threshold1) return {}
    const displayCode = showCode ? `[${code}]` : ''
    return {
        tooltip: (
            <SliderPanel
                title={ code === NITRATES_CODE ? `${i18n.nitrates} ${displayCode}` : `${i18n.pesticides} ${displayCode}`}
                analysis={{ result }}
                threshold={{
                    parameterCode: code,
                    threshold1,
                    threshold2,
                    thresholdType: THRESHOLD_TYPE.INCREASING,
                    display: THRESHOLD_DISPLAY.INCREASING,
                    discriminant: THRESHOLD_DISCRIMINANT.SUP_OR_EQUAL,
                }}
                withThresholdLevels
                overrideColors={compact([THRESHOLD_COLOR.green, threshold2 && THRESHOLD_COLOR.orange, THRESHOLD_COLOR.red])}
            />
        ),
    }
}

const StatisticsTableSummaryNitrates = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
}) => {
    const dispatch = useDispatch()

    const {
        qualitometers,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const thresholdNitrates = useApplicationSetting('CATCHMENT_NITRATES_THRESHOLD', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_NITRATES)
    const endPeriod = useEndPeriod(getEndOfTheYear)

    const tenYearsAgo = getBeginingOfTheYear(getYear(endPeriod) - 10 + 1)
    const fiveYearsAgo = getBeginingOfTheYear(getYear(endPeriod) - 5 + 1)
    const threeYearsAgo = getBeginingOfTheYear(getYear(endPeriod) - 3 + 1)

    const tenLastYearsAnalysis = analysis.filter(a => a.sampleDate >= tenYearsAgo)
    const formattedAnalysis = tenLastYearsAnalysis.filter(a => a.remark === REMARK_CODES.VALID).map(a => ({ ...a, ...calculateThresholdsResult(a, []) }))
    const fiveLastYearsAnalysis = formattedAnalysis.filter(a => a.sampleDate > fiveYearsAgo)
    const threeLastYearsAnalysis = formattedAnalysis.filter(a => a.sampleDate > threeYearsAgo)
    const betweenFiveAndTenYearsAgoAnalysis = formattedAnalysis.filter(a => fiveYearsAgo > a.sampleDate)

    const analysisFormated = useMemo(() => {
        return catchments.map(catchment => {
            const pointsCode = catchmentPoints.filter(point => catchment.id === point.id).map(point => point.codeWithoutDesignation)
            const qualitosIds = qualitometers.filter(q => pointsCode.includes(q.code)).map(q => q.id)

            const threeLastYearsAnalysisNitrates = threeLastYearsAnalysis.filter(a => qualitosIds.includes(a.qualitometer) && a.parameter === NITRATES_CODE)
            const fiveLastYearsAnalysisNitrates = fiveLastYearsAnalysis.filter(a => qualitosIds.includes(a.qualitometer) && a.parameter === NITRATES_CODE)
            const referenceAnalysisNitrates = betweenFiveAndTenYearsAgoAnalysis.filter(a => qualitosIds.includes(a.qualitometer) && a.parameter === NITRATES_CODE)

            const sageThresholdNitrates = catchment?.sageThresholdNitrates ?? thresholdNitrates

            const maxValueNitrates = searchMaxAnalysis(threeLastYearsAnalysisNitrates)
            const p90Nitrates = searchP90Analysis(threeLastYearsAnalysisNitrates)?.result

            const referenceAverageNitrates = calculateAverage(referenceAnalysisNitrates)
            const fiveLastYearsAverageNitrates = calculateAverage(fiveLastYearsAnalysisNitrates)
            const evolutionPercentNitrates = referenceAverageNitrates ? round(fiveLastYearsAverageNitrates / referenceAverageNitrates * 100 -100, 2) : undefined
            const trendNitrates = evolutionPercentNitrates ? evolutionPercentNitrates >= 5 ? i18n.rise : evolutionPercentNitrates <= -5 ? i18n.decrease : i18n.stable : i18n.noDataEnough

            const exceedingAnalysis = fiveLastYearsAnalysisNitrates.filter(a => a.result > sageThresholdNitrates)
            const nbExceedingAnalysis = exceedingAnalysis.length
            const totalAnalysisNitrates = fiveLastYearsAnalysisNitrates.length
            const exceedingYearsNitrates = uniq(exceedingAnalysis.map(a => getYear(a.sampleDate))).sort((a, b) => a - b).reduce((str, year) => `${str} ${year?.toString()}`, '')
            const exceedingFrequency = totalAnalysisNitrates > 0 ? round(nbExceedingAnalysis / totalAnalysisNitrates * 100, 2) : 0

            return {
                id: catchment.id,
                catchment: catchment.code ? `[${catchment.code}] ${catchment.name ?? ''}` : catchment.name || i18n.unknown,
                maxNitrates: {
                    value: maxValueNitrates?.value ? round(maxValueNitrates?.value, 4) : undefined,
                    align: ALIGN.RIGHT,
                    color: getNitratesThresholdColor(maxValueNitrates?.result, thresholdNitrates),
                    ...getTooltip('Nitrates', maxValueNitrates, thresholdNitrates, undefined, showSandreCode),
                },
                percentile90Nitrates: {
                    value: p90Nitrates ? round(p90Nitrates, 4) : undefined,
                    align: ALIGN.RIGHT,
                    color: getNitratesThresholdColor(p90Nitrates, thresholdNitrates),
                    ...getAverageTooltip(p90Nitrates, NITRATES_CODE, thresholdNitrates, undefined, showSandreCode),
                },
                exportMaxNitrates: {
                    value: maxValueNitrates?.value ? round(maxValueNitrates?.value, 4) : undefined,
                    cellType: 'number',
                },
                exportPercentile90Nitrates: {
                    value: p90Nitrates ? round(p90Nitrates, 4) : undefined,
                    align: ALIGN.RIGHT,
                    cellType: 'number',
                },
                trendNitrates,
                sageThresholdNitrates: { value: sageThresholdNitrates, align: ALIGN.RIGHT, cellType: 'number' },
                exceedingFrequency: { value: `${exceedingFrequency} %`, align: ALIGN.RIGHT, cellType: 'right' },
                exceedingNb: { value: nbExceedingAnalysis, align: ALIGN.RIGHT, cellType: 'number', format: '0' },
                exceedingYears: { value: exceedingYearsNitrates, align: ALIGN.RIGHT, cellType: 'string' },
            }
        })
    }, [catchments, catchmentPoints, qualitometers, thresholdNitrates, threeLastYearsAnalysis, fiveLastYearsAnalysis, betweenFiveAndTenYearsAgoAnalysis, showSandreCode])

    const exportAction = useMemo(() =>({
        icon: 'file_download',
        tooltip: i18n.export,
        color: whiteColor,
        onClick: () => {
            const dataOrdered = orderBy(flatten(analysisFormated), ['catchment'], ['asc'])
            const data = dataOrdered.length ? [{
                ...dataOrdered[0],
                headers: [
                    'catchment',
                    'exportMaxNitrates',
                    'exportPercentile90Nitrates',
                    'trendNitrates',
                    'sageThresholdNitrates',
                    'exceedingFrequency',
                    'exceedingNb',
                    'exceedingYears',
                ],
                customHeaders: [
                    'catchment',
                    `${i18n.threeLastYearsMax} ${i18n.nitrates}`,
                    `${i18n.threeLastYearsAveragePercentile90} ${i18n.nitrates}`,
                    `${i18n.fiveLastYearsTrend} ${i18n.nitrates}`,
                    `${i18n.sageObjective} ${i18n.nitrates}`,
                    'overrunTx',
                    'overrunNb',
                    'overrunYears',
                ],
            }, ...dataOrdered.slice(1)] : []
            exportFile({
                data,
                exportType: 'xlsx',
                titleFile: i18n.fiveLastYearsStatistics,
            })
        },
    }), [analysisFormated])

    return (
        <>
            <CardTable
                title={i18n.fiveLastYearsStatistics}
                rows={analysisFormated}
                headers={[
                    { key: 'catchment', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.catchment}</span>) },
                    { key: 'maxNitrates', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.threeLastYearsMax} ${i18n.nitrates}`}</span>) },
                    { key: 'percentile90Nitrates', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.threeLastYearsAveragePercentile90} ${i18n.nitrates}`}</span>) },
                    { key: 'trendNitrates', value: (
                        <Tooltip title={i18n.fiveLastYearsTrendExplanation}>
                            <span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.fiveLastYearsTrend} ${i18n.nitrates}`}</span>
                        </Tooltip>
                    ) },
                    { key: 'sageThresholdNitrates', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.sageObjective} ${i18n.nitrates}`}</span>) },
                    { key: 'exceedingFrequency', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.overrunTx}</span>) },
                    { key: 'exceedingNb', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.overrunNb}</span>) },
                    { key: 'exceedingYears', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.overrunYears}</span>) },
                ]}
                onClickRow={s => dispatch(push(`/station/catchment/${s.id}`))}
                actions={[exportAction]}
                rowsPerPageOptions={nbPerPageLabel}
                color={darkBlue}
                hideNbElements
            />
        </>
    )
}

StatisticsTableSummaryNitrates.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
}

const StatisticsTableSummaryPesticides = ({
    catchments = [],
    catchmentPoints = [],
    analysis = [],
}) => {
    const dispatch = useDispatch()

    const {
        parameters,
        qualityThresholds,
        qualitometers,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        qualityThresholds: store.QualityReducer.qualityThresholds,
        qualitometers: store.QualityReducer.qualitometersLight,
    }), shallowEqual)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const thresholdPesticides1 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_1', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_1)
    const thresholdPesticides2 = useApplicationSetting('CATCHMENT_PESTICIDE_THRESHOLD_2', setting => setting ? parseFloat(setting) : DEFAULT_THRESHOLD_PESTICIDES_2)
    const specificThreshold = useApplicationSetting('CATCHMENT_SPECIFIC_THRESHOLD')
    const threshold = useMemo(() => qualityThresholds.find(t => t.thresholdCode === specificThreshold && t.thresholdType === THRESHOLD.PC), [qualityThresholds, specificThreshold])
    const thresholdsIndexed = useMemo(() => keyBy(threshold?.thresholds ?? [], 'parameterCode'), [threshold?.thresholds])

    const relevantPesticides = useApplicationSetting(CATCHMENT_PESTICIDES, listStringParser)
    const irrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, listStringParser)
    const pesticides = useMemo(() => uniq([...relevantPesticides, ...irrelevantPesticides]), [relevantPesticides, irrelevantPesticides])
    const listSumPesticides = useApplicationSetting(CATCHMENT_LIST_SUM_PESTICIDES, listStringParser)
    const usedSumPesticides = useMemo(() => listSumPesticides.length ? listSumPesticides : pesticides, [listSumPesticides, pesticides])

    const endPeriod = useEndPeriod(getEndOfTheYear)
    const tenYearsAgo = getBeginingOfTheYear(getYear(endPeriod) - 10 + 1)
    const fiveYearsAgo = getBeginingOfTheYear(getYear(endPeriod) - 5 + 1)
    const threeYearsAgo = getBeginingOfTheYear(getYear(endPeriod) - 3 + 1)

    const tenLastYearsAnalysis = analysis.filter(a => a.sampleDate >= tenYearsAgo)
    const formattedAnalysis = tenLastYearsAnalysis.filter(a => a.remark === REMARK_CODES.VALID).map(a => ({ ...a, ...calculateThresholdsResult(a, []) }))
    const fiveLastYearsAnalysis = formattedAnalysis.filter(a => a.sampleDate > fiveYearsAgo)
    const comparisonAnalysisPesticides = tenLastYearsAnalysis.filter(a => a.sampleDate > fiveYearsAgo && pesticides.includes(a.parameter))

    const sumAnalysis = useMemo(() => {
        const filteredAnalysis = formattedAnalysis.filter(a => usedSumPesticides.includes(a.parameter))
        const groupByOperation = groupBy(filteredAnalysis, a => `${a.qualitometer}#${a.operation}`)
        return keys(groupByOperation).map(key => {
            const { [key]: randomName = [] } = groupByOperation
            const formattedOperation = randomName.map(o => ({ ...o, value: parseFloat(o.value) }))
            return {
                result: sumBy(formattedOperation, 'result'),
                value: round(sumBy(formattedOperation, 'value'), 3).toString(),
                sampleDate: formattedOperation[0].sampleDate,
                qualitometer: formattedOperation[0].qualitometer,
                parameter: formattedOperation[0].parameter,
                unit: keys(groupBy(formattedOperation, 'unit')).length === 1 ? formattedOperation[0].unit : '',
            }
        })
    }, [usedSumPesticides, formattedAnalysis])

    const betweenFiveAndTenYearsAgoSumAnalysis = sumAnalysis.filter(a => fiveYearsAgo > a.sampleDate)
    const fiveLastYearsSumAnalysis = sumAnalysis.filter(a => a.sampleDate > fiveYearsAgo)
    const threeLastYearsSumAnalysis = sumAnalysis.filter(a => a.sampleDate > threeYearsAgo)

    const threshold1 = useApplicationSetting(THRESHOLD_S1, setting => setting ? parseFloat(setting) : undefined)
    const threshold2 = useApplicationSetting(THRESHOLD_S2, setting => setting ? parseFloat(setting) : undefined)
    const threshold3 = useApplicationSetting(THRESHOLD_S3, setting => setting ? parseFloat(setting) : undefined)
    const threshold4 = useApplicationSetting(THRESHOLD_S4, setting => setting ? parseFloat(setting) : undefined)

    const analysisFormated = useMemo(() => {
        return catchments.map(catchment => {
            const pointsCode = catchmentPoints.filter(point => catchment.id === point.id).map(point => point.codeWithoutDesignation)
            const qualitosIds = qualitometers.filter(q => pointsCode.includes(q.code)).map(q => q.id)

            const fiveLastYearsAnalysisPesticides = fiveLastYearsAnalysis.filter(a => qualitosIds.includes(a.qualitometer) && pesticides.includes(a.parameter))
            const catchmentComparisonAnalysis = comparisonAnalysisPesticides.filter(a => qualitosIds.includes(a.qualitometer))
            const fiveLastYearsAnalysisPesticidesLabo = fiveLastYearsAnalysis.filter(a => qualitosIds.includes(a.qualitometer) && a.parameter === SUM_PESTICIDES_LABO)

            const threeLastYearsAnalysisSumPesticides = threeLastYearsSumAnalysis.filter(a => qualitosIds.includes(a.qualitometer) && usedSumPesticides.includes(a.parameter))
            const fiveLastYearsAnalysisSumPesticides = fiveLastYearsSumAnalysis.filter(a => qualitosIds.includes(a.qualitometer) && usedSumPesticides.includes(a.parameter))
            const referenceAnalysisSumPesticides = betweenFiveAndTenYearsAgoSumAnalysis.filter(a => qualitosIds.includes(a.qualitometer) && usedSumPesticides.includes(a.parameter))

            const sageThresholdPesticides = catchment?.sageThresholdPesticides ?? thresholdPesticides1
            const sageThresholdSumPesticides = catchment?.sageThresholdSumPesticides ?? thresholdPesticides2

            const nbAnalysisPesticides = fiveLastYearsAnalysisPesticides.length
            const maxValuePesticides = searchMaxAnalysis(threeLastYearsAnalysisSumPesticides)
            const substance = getLabel(parameters, maxValuePesticides?.parameter)
            const p90Pesticides = round(searchP90Analysis(threeLastYearsAnalysisSumPesticides)?.result, 3) || undefined

            const referenceAveragePesticides = calculateAverage(referenceAnalysisSumPesticides)
            const fiveLastYearsAveragePesticides = calculateAverage(fiveLastYearsAnalysisSumPesticides)
            const evolutionPercentPesticides = referenceAveragePesticides ? round(fiveLastYearsAveragePesticides / referenceAveragePesticides * 100 -100, 2) : undefined
            const trendPesticides = evolutionPercentPesticides ? evolutionPercentPesticides >= 5 ? i18n.rise : evolutionPercentPesticides <= -5 ? i18n.decrease : i18n.stable : i18n.noDataEnough

            const exceedingPesticidesAnalysis = fiveLastYearsAnalysisPesticides.filter(a => a.result > sageThresholdPesticides)
            const nbExceedingPesticidesAnalysis = exceedingPesticidesAnalysis.length
            const totalPesticidesAnalysis = fiveLastYearsAnalysisPesticides.length
            const exceedingPesticidesFrequency = totalPesticidesAnalysis > 0 ? round(nbExceedingPesticidesAnalysis / totalPesticidesAnalysis * 100, 2) : 0

            const exceedingSumPesticidesAnalysis = fiveLastYearsAnalysisSumPesticides.filter(a => a.result > sageThresholdSumPesticides)
            const nbExceedingSumPesticidesAnalysis = exceedingSumPesticidesAnalysis.length
            const totalSumPesticidesAnalysis = fiveLastYearsAnalysisSumPesticides.length
            const exceedingSumPesticidesFrequency = totalSumPesticidesAnalysis > 0 ? round(nbExceedingSumPesticidesAnalysis / totalSumPesticidesAnalysis * 100, 2) : 0

            const maxAnnualSumPesticidesFromLabo = maxBy(fiveLastYearsAnalysisPesticidesLabo, 'result')?.result
            const quantificationsPercent = catchmentComparisonAnalysis.length ? round(fiveLastYearsAnalysisPesticides.length / catchmentComparisonAnalysis.length * 100, 2) : 0
            const distinctQuantificationsNb = keys(groupBy(fiveLastYearsAnalysisPesticides, 'parameter')).length

            const threshold1Overruns = fiveLastYearsAnalysisPesticides.filter(a => a.result > (thresholdsIndexed[a.parameter]?.threshold1 ?? threshold1))
            const threshold2Overruns = isEmpty(thresholdsIndexed) ? fiveLastYearsAnalysisSumPesticides.filter(a => a.result > threshold2) ?? [] : fiveLastYearsAnalysisPesticides.filter(a => a.result > (thresholdsIndexed[a.parameter]?.threshold2 ?? threshold2))
            const threshold3Overruns = fiveLastYearsAnalysisPesticides.filter(a => a.result > (thresholdsIndexed[a.parameter]?.threshold3 ?? threshold3))
            const threshold4Overruns = isEmpty(thresholdsIndexed) ? fiveLastYearsAnalysisSumPesticides.filter(a => a.result > threshold4) ?? [] : fiveLastYearsAnalysisPesticides.filter(a => a.result > (thresholdsIndexed[a.parameter]?.threshold4 ?? threshold4))
            const threshold1OverrunsNb = threshold1Overruns.length
            const threshold2OverrunsNb = threshold2Overruns.length
            const threshold3OverrunsNb = threshold3Overruns.length
            const threshold4OverrunsNb = threshold4Overruns.length
            const exceedingYearsPesticides = uniq([...threshold3Overruns, ...threshold4Overruns].map(a => getYear(a.sampleDate))).sort((a, b) => a - b).reduce((str, year) => `${str} ${year?.toString()}`, '')
            const totalAnalysisReference = isEmpty(thresholdsIndexed) ? totalSumPesticidesAnalysis : totalPesticidesAnalysis

            return {
                id: catchment.id,
                catchment: catchment.code ? `[${catchment.code}] ${catchment.name ?? ''}` : catchment.name || i18n.unknown,
                nbAnalysisPesticides: { value: nbAnalysisPesticides, align: ALIGN.RIGHT, cellType: 'number', format: '0' },
                maxPesticides: {
                    value: maxValuePesticides?.value ? round(maxValuePesticides?.value, 4) : undefined,
                    align: ALIGN.RIGHT,
                    color: getPesticidesThresholdColor(maxValuePesticides?.result, threshold1, threshold3),
                    ...getTooltip(substance, maxValuePesticides, threshold1, threshold3, showSandreCode),
                },
                percentile90Pesticides: {
                    value: p90Pesticides ? round(p90Pesticides, 4) : undefined,
                    align: ALIGN.RIGHT,
                    color: getPesticidesThresholdColor(p90Pesticides, threshold1, threshold3),
                    ...getAverageTooltip(p90Pesticides, SUM_PESTICIDES_LABO, threshold1, threshold3, showSandreCode),
                },
                exportMaxPesticides: {
                    value: maxValuePesticides?.value ? round(maxValuePesticides?.value, 4) : undefined,
                    cellType: 'right',
                },
                exportPercentile90Pesticides: {
                    value: p90Pesticides ? round(p90Pesticides, 4) : undefined,
                    cellType: 'number',
                },
                trendPesticides,
                sageThresholdPesticides: { value: sageThresholdPesticides, align: ALIGN.RIGHT, cellType: 'number' },
                exceedingFrequencyPesticides: { value: `${exceedingPesticidesFrequency} %`, align: ALIGN.RIGHT, cellType: 'right' },
                exceedingNbPesticides: { value: nbExceedingPesticidesAnalysis, align: ALIGN.RIGHT, cellType: 'number', format: '0' },
                sageThresholdSumPesticides: { value: sageThresholdSumPesticides, align: ALIGN.RIGHT, cellType: 'number' },
                exceedingFrequencySumPesticides: { value: `${exceedingSumPesticidesFrequency} %`, align: ALIGN.RIGHT, cellType: 'right' },
                exceedingNbSumPesticides: { value: nbExceedingSumPesticidesAnalysis, align: ALIGN.RIGHT, cellType: 'number', format: '0' },
                maxAnnualSumPesticidesFromLabo: { value: maxAnnualSumPesticidesFromLabo ? round(maxAnnualSumPesticidesFromLabo, 4) : undefined, align: ALIGN.RIGHT, cellType: 'number' },
                quantificationsPercent: { value: `${quantificationsPercent} %`, align: ALIGN.RIGHT, cellType: 'right' },
                distinctQuantificationsNb: { value: distinctQuantificationsNb, align: ALIGN.RIGHT, cellType: 'number', format: '0' },
                threshold1OverrunsNb: { value: threshold1OverrunsNb, align: ALIGN.RIGHT, cellType: 'number', format: '0' },
                threshold1OverrunsFrequency: { value: totalPesticidesAnalysis > 0 ? `${round(threshold1OverrunsNb / totalPesticidesAnalysis * 100, 2)} %` : '0 %', align: ALIGN.RIGHT, cellType: 'right' },
                threshold2OverrunsNb: { value: threshold2OverrunsNb, align: ALIGN.RIGHT, cellType: 'number', format: '0' },
                threshold2OverrunsFrequency: { value: totalAnalysisReference > 0 ? `${round(threshold2OverrunsNb / totalAnalysisReference * 100, 2)} %` : '0 %', align: ALIGN.RIGHT, cellType: 'right' },
                threshold3OverrunsNb: { value: threshold3OverrunsNb, align: ALIGN.RIGHT, cellType: 'number', format: '0' },
                threshold3OverrunsFrequency: { value: totalPesticidesAnalysis > 0 ? `${round(threshold3OverrunsNb / totalPesticidesAnalysis * 100, 2)} %` : '0 %', align: ALIGN.RIGHT, cellType: 'right' },
                threshold4OverrunsNb: { value: threshold4OverrunsNb, align: ALIGN.RIGHT, cellType: 'number', format: '0' },
                threshold4OverrunsFrequency: { value: totalAnalysisReference > 0 ? `${round(threshold4OverrunsNb / totalAnalysisReference * 100, 2)} %` : '0 %', align: ALIGN.RIGHT, cellType: 'right' },
                overrunYears: { value: exceedingYearsPesticides, align: ALIGN.RIGHT, cellType: 'string' },
            }
        })
    }, [catchments, catchmentPoints, qualitometers, fiveLastYearsAnalysis, threshold1, threshold2, threshold3, threshold4, thresholdPesticides1, thresholdPesticides2, thresholdsIndexed, parameters, comparisonAnalysisPesticides, pesticides, usedSumPesticides, betweenFiveAndTenYearsAgoSumAnalysis, fiveLastYearsSumAnalysis, threeLastYearsSumAnalysis, showSandreCode])

    const exportAction = useMemo(() =>({
        icon: 'file_download',
        tooltip: i18n.export,
        color: whiteColor,
        onClick: () => {
            const thresholdOverrunNb = i18n.thresholdOverrunNb
            const cutThresholdOverrunNb = thresholdOverrunNb.split('${ threshold }')[0]
            const thresholdOverrunTx = i18n.thresholdOverrunTx
            const cutThresholdOverrunTx = thresholdOverrunTx.split('${ threshold }')[0]
            const overrunYears = i18n.overrunYears

            const dataOrdered = orderBy(flatten(analysisFormated), ['catchment'], ['asc'])
            const data = dataOrdered.length ? [{
                ...dataOrdered[0],
                headers: [
                    'catchment',
                    'nbAnalysisPesticides',
                    'exportMaxPesticides',
                    'exportPercentile90Pesticides',
                    'trendPesticides',
                    'sageThresholdPesticides',
                    'exceedingFrequencyPesticides',
                    'exceedingNbPesticides',
                    'sageThresholdSumPesticides',
                    'exceedingFrequencySumPesticides',
                    'exceedingNbSumPesticides',
                    'maxAnnualSumPesticidesFromLabo',
                    'quantificationsPercent',
                    'distinctQuantificationsNb',
                    'threshold1OverrunsNb',
                    'threshold1OverrunsFrequency',
                    'threshold2OverrunsNb',
                    'threshold2OverrunsFrequency',
                    'threshold3OverrunsNb',
                    'threshold3OverrunsFrequency',
                    'threshold4OverrunsNb',
                    'threshold4OverrunsFrequency',
                    'overrunYears',
                ],
                customHeaders: [
                    'catchment',
                    'nbQuantificationsPesticides',
                    `${i18n.threeLastYearsMax} ${i18n.pesticides}`,
                    `${i18n.threeLastYearsAveragePercentile90} ${i18n.pesticides}`,
                    `${i18n.fiveLastYearsTrend} ${i18n.pesticides}`,
                    `${i18n.sageObjective} ${i18n.pesticides}`,
                    `${i18n.overrunTx} ${i18n.pesticides}`,
                    `${i18n.overrunNb} ${i18n.pesticides}`,
                    `${i18n.sageObjective} ${i18n.sumPest}`,
                    `${i18n.overrunTx} ${i18n.sumPest}`,
                    `${i18n.overrunNb} ${i18n.sumPest}`,
                    'maxAnnualSumPesticidesFromLabo',
                    'percentOfQuantifications',
                    'nbSubstancesQuantified',
                    `${cutThresholdOverrunNb} S1`,
                    `${cutThresholdOverrunTx} S1`,
                    `${cutThresholdOverrunNb} S2`,
                    `${cutThresholdOverrunTx} S2`,
                    `${cutThresholdOverrunNb} S3`,
                    `${cutThresholdOverrunTx} S3`,
                    `${cutThresholdOverrunNb} S4`,
                    `${cutThresholdOverrunTx} S4`,
                    `${overrunYears} S3 / S4`,
                ],
            }, ...dataOrdered.slice(1)] : []
            exportFile({
                data,
                exportType: 'xlsx',
                titleFile: i18n.fiveLastYearsStatistics,
            })
        },
    }), [analysisFormated])

    return (
        <>
            <CardTable
                title={i18n.fiveLastYearsStatistics}
                rows={analysisFormated}
                headers={[
                    { key: 'catchment', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.catchment}</span>) },
                    { key: 'nbAnalysisPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.nbQuantificationsPesticides}</span>) },
                    { key: 'maxPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.threeLastYearsMax} ${i18n.pesticides}`}</span>) },
                    { key: 'percentile90Pesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.threeLastYearsAveragePercentile90} ${i18n.pesticides}`}</span>) },
                    { key: 'trendPesticides', value: (
                        <Tooltip title={i18n.fiveLastYearsTrendExplanation}>
                            <span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.fiveLastYearsTrend} ${i18n.pesticides}`}</span>
                        </Tooltip>
                    ) },
                    { key: 'sageThresholdPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.sageObjective} ${i18n.pesticides}`}</span>) },
                    { key: 'exceedingFrequencyPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.overrunTx} ${i18n.pesticides}`}</span>) },
                    { key: 'exceedingNbPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.overrunNb} ${i18n.pesticides}`}</span>) },
                    { key: 'sageThresholdSumPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.sageObjective} ${i18n.sumPest}`}</span>) },
                    { key: 'exceedingFrequencySumPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.overrunTx} ${i18n.sumPest}`}</span>) },
                    { key: 'exceedingNbSumPesticides', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.overrunNb} ${i18n.sumPest}`}</span>) },
                    { key: 'maxAnnualSumPesticidesFromLabo', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.maxAnnualSumPesticidesFromLabo}</span>) },
                    { key: 'quantificationsPercent', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.percentOfQuantifications}</span>) },
                    { key: 'distinctQuantificationsNb', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{i18n.nbSubstancesQuantified}</span>) },
                    { key: 'threshold1OverrunsNb', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{template(i18n.thresholdOverrunNb)({ threshold: 'S1' })}</span>) },
                    { key: 'threshold1OverrunsFrequency', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{template(i18n.thresholdOverrunTx)({ threshold: 'S1' })}</span>) },
                    { key: 'threshold2OverrunsNb', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{template(i18n.thresholdOverrunNb)({ threshold: 'S2' })}</span>) },
                    { key: 'threshold2OverrunsFrequency', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{template(i18n.thresholdOverrunTx)({ threshold: 'S2' })}</span>) },
                    { key: 'threshold3OverrunsNb', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{template(i18n.thresholdOverrunNb)({ threshold: 'S3' })}</span>) },
                    { key: 'threshold3OverrunsFrequency', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{template(i18n.thresholdOverrunTx)({ threshold: 'S3' })}</span>) },
                    { key: 'threshold4OverrunsNb', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{template(i18n.thresholdOverrunNb)({ threshold: 'S4' })}</span>) },
                    { key: 'threshold4OverrunsFrequency', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{template(i18n.thresholdOverrunTx)({ threshold: 'S4' })}</span>) },
                    { key: 'overrunYears', value: (<span style={{ whiteSpace: 'pre-wrap' }}>{`${i18n.overrunYears} S3 / S4`}</span>) },
                ]}
                onClickRow={s => dispatch(push(`/station/catchment/${s.id}`))}
                actions={[exportAction]}
                rowsPerPageOptions={nbPerPageLabel}
                color={darkBlue}
                hideNbElements
            />
        </>
    )
}

StatisticsTableSummaryPesticides.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentDto)),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisUltraLight)),
}

export { StatisticsTableSummaryPesticides, StatisticsTableSummaryNitrates }